import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { staffAPI } from '../../../config/api';
import APP_COLOURS from '../../../config/colors';
import { utils, writeFile } from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function currencyFormat(num) {
  num = parseFloat(num) || 0;
  return 'R ' + num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

const formatTime = (timeValue) => {
  if (!timeValue) return '--';
  try {
    if (timeValue._seconds || (timeValue.seconds && timeValue.nanoseconds)) {
      const ts = timeValue._seconds || timeValue.seconds;
      return moment.unix(ts).format('HH:mm');
    }
    return moment(timeValue).format('HH:mm');
  } catch {
    return '--';
  }
};

const StaffSalesReport = ({ staffData, onClose, initialStartDate, initialEndDate }) => {
  const [startDate, setStartDate] = useState(
    initialStartDate || moment().subtract(30, 'days').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(
    initialEndDate || moment().format('YYYY-MM-DD')
  );
  const [sales, setSales] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasData, setHasData] = useState(false);

  const staffId = staffData?.id;
  const staffName = `${staffData?.first_name || ''} ${staffData?.last_name || ''}`.trim();
  const staffPosition = staffData?.position || 'N/A';
  const staffMall = staffData?.mall?.title || staffData?.mall || 'N/A';
  const staffRetailer = staffData?.retailer?.title || staffData?.retailer || 'N/A';
  const staffEmail = staffData?.email || 'N/A';
  const staffPhone = staffData?.phone || 'N/A';
  const staffBrands = (staffData?.brands || [])
    .map(b => (typeof b === 'object' ? b.title : b))
    .filter(Boolean)
    .join(', ') || 'N/A';

  const fetchReportData = async () => {
    if (!staffId) return;
    setLoading(true);
    setHasData(false);
    try {
      const [salesRes, attendanceRes] = await Promise.all([
        staffAPI.getStaffSales(staffId, { startDate, endDate }),
        staffAPI.getStaffAttendance(staffId, { startDate, endDate }),
      ]);

      const salesArr = Array.isArray(salesRes)
        ? salesRes
        : Array.isArray(salesRes?.sales)
        ? salesRes.sales
        : [];

      const attendanceArr = Array.isArray(attendanceRes)
        ? attendanceRes
        : Array.isArray(attendanceRes?.attendance)
        ? attendanceRes.attendance
        : Array.isArray(attendanceRes?.data)
        ? attendanceRes.data
        : [];

      setSales(salesArr);
      setAttendance(attendanceArr);
      setHasData(true);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setSales([]);
      setAttendance([]);
    } finally {
      setLoading(false);
    }
  };

  // Metrics derived from sales
  const salesMetrics = React.useMemo(() => {
    if (!sales.length) return { totalSales: 0, totalTransactions: 0, avgSale: 0, ipt: 0, totalUnits: 0 };
    const totalSales = sales.reduce((s, x) => s + (parseFloat(x.trs) || 0), 0);
    const totalUnits = sales.reduce((s, x) => {
      if (x.items && Array.isArray(x.items)) return s + x.items.length;
      return s + (parseInt(x.ipt) || 0);
    }, 0);
    return {
      totalSales,
      totalTransactions: sales.length,
      avgSale: sales.length ? totalSales / sales.length : 0,
      ipt: sales.length ? totalUnits / sales.length : 0,
      totalUnits,
    };
  }, [sales]);

  // Attendance summary — compute against the full date range
  const attendanceSummary = React.useMemo(() => {
    const start = moment(startDate);
    const end = moment(endDate);
    const days = end.diff(start, 'days') + 1;
    let daysWorked = 0;
    let newCustomers = 0;
    let existingCustomers = 0;

    for (let i = 0; i < days; i++) {
      const target = start.clone().add(i, 'days').format('YYYY-MM-DD');
      const record = attendance.find(d => {
        let dateField = d.date || d.dateCreated || d.checkin || d.clockIn;
        if (!dateField) return false;
        if (dateField._seconds || (dateField.seconds && dateField.nanoseconds)) {
          const ts = dateField._seconds || dateField.seconds;
          dateField = moment.unix(ts).format('YYYY-MM-DD');
        }
        return moment(dateField).format('YYYY-MM-DD') === target;
      });
      if (record) {
        daysWorked++;
        newCustomers += parseInt(record.newCustomer || record.new_customer) || 0;
        existingCustomers += parseInt(record.existCustomer || record.exist_customer || record.existingCustomer) || 0;
      }
    }
    return { daysWorked, newCustomers, existingCustomers, totalCustomers: newCustomers + existingCustomers };
  }, [attendance, startDate, endDate]);

  // Sorted sales for display
  const sortedSales = [...sales].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  // ------------------------------------------------------------------
  // EXCEL EXPORT
  // ------------------------------------------------------------------
  const handleExportExcel = () => {
    const wb = utils.book_new();

    // Sheet 1 — Summary
    const summaryRows = [
      ['TRAQ SALES REPORT', ''],
      ['Generated', moment().format('DD MMM YYYY HH:mm')],
      [''],
      ['STAFF DETAILS', ''],
      ['Name', staffName],
      ['Position', staffPosition],
      ['Mall', staffMall],
      ['Retailer', staffRetailer],
      ['Email', staffEmail],
      ['Phone', staffPhone],
      ['Brands', staffBrands],
      [''],
      ['REPORT PERIOD', ''],
      ['From', moment(startDate).format('DD MMM YYYY')],
      ['To', moment(endDate).format('DD MMM YYYY')],
      [''],
      ['SALES SUMMARY', ''],
      ['Total Sales (R)', salesMetrics.totalSales.toFixed(2)],
      ['Total Transactions', salesMetrics.totalTransactions],
      ['Average Rand Sale (R)', salesMetrics.avgSale.toFixed(2)],
      ['Items per Transaction', salesMetrics.ipt.toFixed(2)],
      ['Total Units Sold', salesMetrics.totalUnits],
      [''],
      ['ATTENDANCE SUMMARY', ''],
      ['Days Worked', attendanceSummary.daysWorked],
      ['New Customers', attendanceSummary.newCustomers],
      ['Existing Customers', attendanceSummary.existingCustomers],
      ['Total Customers', attendanceSummary.totalCustomers],
    ];
    const wsSummary = utils.aoa_to_sheet(summaryRows);
    wsSummary['!cols'] = [{ wch: 28 }, { wch: 40 }];
    utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Sheet 2 — Sales Transactions
    const salesHeaders = ['Date', 'Time', 'Items', 'Brands', 'Qty', 'Avg Rand Sale (R)', 'Total Sale (R)'];
    const salesRows = sortedSales.map(sale => [
      moment(sale.dateCreated).format('DD MMM YYYY'),
      moment(sale.dateCreated).format('HH:mm'),
      (sale.items || []).map(i => `${i.name || 'Item'}${i.returned ? ' [Returned]' : ''} x${i.quantity || 1}`).join('; '),
      (sale.brands || []).join(', '),
      sale.ipt || (sale.items && sale.items.length) || 0,
      parseFloat(sale.ars || 0).toFixed(2),
      parseFloat(sale.trs || 0).toFixed(2),
    ]);
    const wsSales = utils.aoa_to_sheet([salesHeaders, ...salesRows]);
    wsSales['!cols'] = [{ wch: 16 }, { wch: 8 }, { wch: 60 }, { wch: 30 }, { wch: 6 }, { wch: 20 }, { wch: 16 }];
    utils.book_append_sheet(wb, wsSales, 'Sales Transactions');

    // Sheet 3 — Daily Attendance
    const attHeaders = ['Date', 'Day', 'Check-in', 'Lunch Start', 'Lunch End', 'Check-out', 'New Customers', 'Exist Customers', 'IPT', 'ARS (R)', 'TRS (R)'];
    const start = moment(startDate);
    const end = moment(endDate);
    const days = end.diff(start, 'days') + 1;
    const attRows = [];
    for (let i = 0; i < days; i++) {
      const date = start.clone().add(i, 'days');
      const target = date.format('YYYY-MM-DD');
      const rec = attendance.find(d => {
        let df = d.date || d.dateCreated || d.checkin || d.clockIn;
        if (!df) return false;
        if (df._seconds || (df.seconds && df.nanoseconds)) {
          df = moment.unix(df._seconds || df.seconds).format('YYYY-MM-DD');
        }
        return moment(df).format('YYYY-MM-DD') === target;
      });
      attRows.push([
        date.format('DD MMM YYYY'),
        date.format('ddd'),
        rec ? formatTime(rec.checkin || rec.clockIn) : '--',
        rec ? formatTime(rec.lunch_start || rec.lunchStart) : '--',
        rec ? formatTime(rec.lunch_end || rec.lunchEnd) : '--',
        rec ? formatTime(rec.checkout || rec.clockOut) : '--',
        rec ? (parseInt(rec.newCustomer || rec.new_customer) || 0) : '--',
        rec ? (parseInt(rec.existCustomer || rec.exist_customer || rec.existingCustomer) || 0) : '--',
        rec ? (rec.ipt || rec.IPT || '--') : '--',
        rec && (rec.ars || rec.ARS) ? parseFloat(rec.ars || rec.ARS).toFixed(2) : '--',
        rec && (rec.trs || rec.TRS) ? parseFloat(rec.trs || rec.TRS).toFixed(2) : '--',
      ]);
    }
    const wsAtt = utils.aoa_to_sheet([attHeaders, ...attRows]);
    wsAtt['!cols'] = [{ wch: 16 }, { wch: 6 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 6 }, { wch: 12 }, { wch: 12 }];
    utils.book_append_sheet(wb, wsAtt, 'Attendance');

    const fileName = `TRAQ_Sales_Report_${staffName.replace(/\s+/g, '_')}_${moment(startDate).format('DD_MMM')}_to_${moment(endDate).format('DD_MMM_YYYY')}.xlsx`;
    writeFile(wb, fileName);
  };

  // ------------------------------------------------------------------
  // PDF EXPORT
  // ------------------------------------------------------------------
  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;

    // Header bar
    doc.setFillColor(0, 104, 255);
    doc.rect(0, 0, pageWidth, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('TRAQ — Sales Report', margin, 13);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${moment().format('DD MMM YYYY HH:mm')}`, pageWidth - margin, 13, { align: 'right' });

    // Period pill
    doc.setFillColor(26, 31, 40);
    doc.roundedRect(margin, 24, pageWidth - margin * 2, 12, 2, 2, 'F');
    doc.setTextColor(119, 119, 119);
    doc.setFontSize(8);
    doc.text('Report Period', margin + 4, 31);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(
      `${moment(startDate).format('DD MMM YYYY')}  →  ${moment(endDate).format('DD MMM YYYY')}`,
      pageWidth / 2, 31, { align: 'center' }
    );

    let y = 42;

    // Staff Details section header
    doc.setFillColor(22, 26, 34);
    doc.rect(margin, y, pageWidth - margin * 2, 7, 'F');
    doc.setTextColor(0, 104, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('STAFF DETAILS', margin + 3, y + 4.8);
    y += 10;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, textColor: [255, 255, 255], fillColor: [22, 26, 34], lineColor: [36, 40, 48], lineWidth: 0.2 },
      headStyles: { fillColor: [26, 31, 40], textColor: [119, 119, 119], fontStyle: 'normal' },
      alternateRowStyles: { fillColor: [26, 31, 40] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45, textColor: [119, 119, 119] }, 1: { textColor: [255, 255, 255] } },
      body: [
        ['Name', staffName],
        ['Position', staffPosition],
        ['Mall / Location', staffMall],
        ['Retailer', staffRetailer],
        ['Email', staffEmail],
        ['Phone', staffPhone],
        ['Brands', staffBrands],
      ],
    });
    y = doc.lastAutoTable.finalY + 8;

    // Sales Summary header
    doc.setFillColor(22, 26, 34);
    doc.rect(margin, y, pageWidth - margin * 2, 7, 'F');
    doc.setTextColor(0, 104, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('SALES SUMMARY', margin + 3, y + 4.8);
    y += 10;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, textColor: [255, 255, 255], fillColor: [22, 26, 34], lineColor: [36, 40, 48], lineWidth: 0.2 },
      alternateRowStyles: { fillColor: [26, 31, 40] },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: [119, 119, 119], cellWidth: 70 },
        1: { textColor: [7, 213, 93], fontStyle: 'bold', halign: 'right' },
      },
      body: [
        ['Total Sales', currencyFormat(salesMetrics.totalSales)],
        ['Total Transactions', salesMetrics.totalTransactions.toString()],
        ['Average Rand Sale', currencyFormat(salesMetrics.avgSale)],
        ['Items per Transaction', salesMetrics.ipt.toFixed(2)],
        ['Total Units Sold', salesMetrics.totalUnits.toString()],
      ],
    });
    y = doc.lastAutoTable.finalY + 8;

    // Attendance Summary header
    doc.setFillColor(22, 26, 34);
    doc.rect(margin, y, pageWidth - margin * 2, 7, 'F');
    doc.setTextColor(0, 104, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('ATTENDANCE SUMMARY', margin + 3, y + 4.8);
    y += 10;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, textColor: [255, 255, 255], fillColor: [22, 26, 34], lineColor: [36, 40, 48], lineWidth: 0.2 },
      alternateRowStyles: { fillColor: [26, 31, 40] },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: [119, 119, 119], cellWidth: 70 },
        1: { textColor: [0, 104, 255], fontStyle: 'bold', halign: 'right' },
      },
      body: [
        ['Days Worked', attendanceSummary.daysWorked.toString()],
        ['New Customers', attendanceSummary.newCustomers.toString()],
        ['Existing Customers', attendanceSummary.existingCustomers.toString()],
        ['Total Customers', attendanceSummary.totalCustomers.toString()],
      ],
    });
    y = doc.lastAutoTable.finalY + 8;

    // Sales Transactions table header
    doc.setFillColor(22, 26, 34);
    doc.rect(margin, y, pageWidth - margin * 2, 7, 'F');
    doc.setTextColor(0, 104, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(`SALES TRANSACTIONS (${sortedSales.length})`, margin + 3, y + 4.8);
    y += 10;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: 2.5, textColor: [255, 255, 255], fillColor: [22, 26, 34], lineColor: [36, 40, 48], lineWidth: 0.2, overflow: 'linebreak' },
      headStyles: { fillColor: [26, 31, 40], textColor: [119, 119, 119], fontStyle: 'bold', fontSize: 7.5 },
      alternateRowStyles: { fillColor: [26, 31, 40] },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 60 },
        2: { cellWidth: 22 },
        3: { cellWidth: 22, halign: 'right', textColor: [7, 213, 93], fontStyle: 'bold' },
      },
      head: [['Date', 'Items', 'Qty', 'Total (R)']],
      body: sortedSales.map(sale => [
        moment(sale.dateCreated).format('DD MMM YY'),
        (sale.items || []).map(i => `${i.name || 'Item'}${i.returned ? ' [R]' : ''} ×${i.quantity || 1}`).join('\n') || 'N/A',
        sale.ipt || (sale.items && sale.items.length) || 0,
        currencyFormat(sale.trs || 0),
      ]),
    });

    // Footer on each page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(119, 119, 119);
      doc.text(
        `TRAQ Sales Report  •  ${staffName}  •  Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 6,
        { align: 'center' }
      );
    }

    const fileName = `TRAQ_Sales_Report_${staffName.replace(/\s+/g, '_')}_${moment(startDate).format('DD_MMM')}_to_${moment(endDate).format('DD_MMM_YYYY')}.pdf`;
    doc.save(fileName);
  };

  const applyPreset = (days) => {
    setStartDate(moment().subtract(days, 'days').format('YYYY-MM-DD'));
    setEndDate(moment().format('YYYY-MM-DD'));
    setHasData(false);
  };

  const presets = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
    { label: 'YTD', days: moment().dayOfYear() - 1 },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <div
        className="flex flex-col rounded-xl shadow-2xl overflow-hidden"
        style={{
          backgroundColor: APP_COLOURS.BG,
          border: `1px solid ${APP_COLOURS.BG4}`,
          width: '96vw',
          maxWidth: '960px',
          maxHeight: '94vh',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ---- MODAL HEADER ---- */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${APP_COLOURS.BG4}`, backgroundColor: APP_COLOURS.BG3 }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-9 h-9 rounded-lg"
              style={{ backgroundColor: `${APP_COLOURS.BLUE}20` }}
            >
              <i className="fas fa-file-chart-bar text-base" style={{ color: APP_COLOURS.BLUE }}></i>
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                Sales Report
              </h2>
              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                {staffName} • {staffPosition} • {staffMall}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-all"
            style={{ backgroundColor: APP_COLOURS.BG4, color: APP_COLOURS.TEXTCOLOR }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = APP_COLOURS.BG5)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = APP_COLOURS.BG4)}
          >
            <i className="fas fa-times text-sm"></i>
          </button>
        </div>

        {/* ---- SCROLLABLE BODY ---- */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Date Range Filter */}
          <div
            className="p-4 rounded-lg"
            style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}
          >
            <p className="text-xs font-semibold mb-3" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              <i className="fas fa-calendar-alt mr-1.5" style={{ color: APP_COLOURS.BLUE }}></i>
              SELECT REPORT PERIOD
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-xs whitespace-nowrap" style={{ color: APP_COLOURS.PLACEHOLDER }}>From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => { setStartDate(e.target.value); setHasData(false); }}
                  className="px-3 py-1.5 rounded text-xs focus:outline-none"
                  style={{ backgroundColor: APP_COLOURS.BG, border: `1px solid ${APP_COLOURS.BG4}`, color: APP_COLOURS.TEXTCOLOR }}
                  onFocus={e => (e.currentTarget.style.borderColor = APP_COLOURS.BLUE)}
                  onBlur={e => (e.currentTarget.style.borderColor = APP_COLOURS.BG4)}
                />
              </div>
              <i className="fas fa-arrow-right text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
              <div className="flex items-center gap-2">
                <label className="text-xs whitespace-nowrap" style={{ color: APP_COLOURS.PLACEHOLDER }}>To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => { setEndDate(e.target.value); setHasData(false); }}
                  className="px-3 py-1.5 rounded text-xs focus:outline-none"
                  style={{ backgroundColor: APP_COLOURS.BG, border: `1px solid ${APP_COLOURS.BG4}`, color: APP_COLOURS.TEXTCOLOR }}
                  onFocus={e => (e.currentTarget.style.borderColor = APP_COLOURS.BLUE)}
                  onBlur={e => (e.currentTarget.style.borderColor = APP_COLOURS.BG4)}
                />
              </div>

              <div className="h-5 w-px mx-1" style={{ backgroundColor: APP_COLOURS.BG4 }}></div>

              {presets.map(p => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p.days)}
                  className="px-2.5 py-1.5 rounded text-xs font-medium"
                  style={{ backgroundColor: APP_COLOURS.BG4, color: APP_COLOURS.TEXTCOLOR, border: `1px solid ${APP_COLOURS.BG5}` }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = APP_COLOURS.BLUE)}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = APP_COLOURS.BG5)}
                >
                  {p.label}
                </button>
              ))}

              <button
                onClick={fetchReportData}
                disabled={loading}
                className="px-4 py-1.5 rounded text-xs font-semibold ml-auto"
                style={{
                  backgroundColor: loading ? APP_COLOURS.BG5 : APP_COLOURS.BLUE,
                  color: '#fff',
                  opacity: loading ? 0.6 : 1,
                  minWidth: '120px',
                }}
              >
                {loading ? (
                  <><i className="fas fa-spinner fa-spin mr-1.5"></i>Loading...</>
                ) : (
                  <><i className="fas fa-search mr-1.5"></i>Generate Report</>
                )}
              </button>
            </div>
          </div>

          {/* Empty prompt */}
          {!hasData && !loading && (
            <div
              className="flex flex-col items-center justify-center py-16 rounded-lg"
              style={{ backgroundColor: APP_COLOURS.BG3, border: `1px dashed ${APP_COLOURS.BG5}` }}
            >
              <i className="fas fa-chart-bar text-4xl mb-3" style={{ color: APP_COLOURS.BG5 }}></i>
              <p className="text-sm font-medium mb-1" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                Select a period and generate the report
              </p>
              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                Choose a date range above, then click <strong style={{ color: APP_COLOURS.BLUE }}>Generate Report</strong>
              </p>
            </div>
          )}

          {/* Report content — only shown once data is loaded */}
          {hasData && !loading && (
            <>
              {/* Staff Details Card */}
              <div
                className="p-4 rounded-lg"
                style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}
              >
                <p className="text-xs font-semibold mb-3 flex items-center gap-1.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  <i className="fas fa-user-circle" style={{ color: APP_COLOURS.BLUE }}></i>
                  STAFF DETAILS
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: 'Name', value: staffName },
                    { label: 'Position', value: staffPosition },
                    { label: 'Mall', value: staffMall },
                    { label: 'Retailer', value: staffRetailer },
                    { label: 'Email', value: staffEmail },
                    { label: 'Phone', value: staffPhone },
                    { label: 'Brands', value: staffBrands },
                    {
                      label: 'Period',
                      value: `${moment(startDate).format('DD MMM YYYY')} → ${moment(endDate).format('DD MMM YYYY')}`,
                    },
                  ].map(({ label, value }) => (
                    <div key={label} className="p-2.5 rounded" style={{ backgroundColor: APP_COLOURS.BG4 }}>
                      <p className="text-xs mb-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>{label}</p>
                      <p className="text-xs font-semibold truncate" style={{ color: APP_COLOURS.TEXTCOLOR }} title={value}>
                        {value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* KPI Cards Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: 'fa-dollar-sign', label: 'Total Sales', value: currencyFormat(salesMetrics.totalSales), color: APP_COLOURS.GREEN },
                  { icon: 'fa-receipt', label: 'Transactions', value: salesMetrics.totalTransactions, color: APP_COLOURS.BLUE },
                  { icon: 'fa-chart-line', label: 'Avg Rand Sale', value: currencyFormat(salesMetrics.avgSale), color: APP_COLOURS.BLUE },
                  { icon: 'fa-shopping-bag', label: 'Items / Transaction', value: salesMetrics.ipt.toFixed(2), color: APP_COLOURS.PLACEHOLDER },
                ].map(({ icon, label, value, color }) => (
                  <div key={label} className="p-3 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`fas ${icon} text-sm`} style={{ color }}></i>
                      <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>{label}</p>
                    </div>
                    <p className="text-lg font-bold" style={{ color }}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Attendance KPIs Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: 'fa-calendar-check', label: 'Days Worked', value: attendanceSummary.daysWorked, color: APP_COLOURS.BLUE },
                  { icon: 'fa-user-plus', label: 'New Customers', value: attendanceSummary.newCustomers, color: APP_COLOURS.GREEN },
                  { icon: 'fa-users', label: 'Existing Customers', value: attendanceSummary.existingCustomers, color: APP_COLOURS.BLUE },
                  { icon: 'fa-user-friends', label: 'Total Customers', value: attendanceSummary.totalCustomers, color: APP_COLOURS.PLACEHOLDER },
                ].map(({ icon, label, value, color }) => (
                  <div key={label} className="p-3 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`fas ${icon} text-sm`} style={{ color }}></i>
                      <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>{label}</p>
                    </div>
                    <p className="text-lg font-bold" style={{ color }}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Sales Transactions Table */}
              <div
                className="rounded-lg overflow-hidden"
                style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}
              >
                <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${APP_COLOURS.BG4}` }}>
                  <p className="text-sm font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    <i className="fas fa-list mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
                    Sales Transactions ({sortedSales.length})
                  </p>
                </div>
                {sortedSales.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead style={{ backgroundColor: APP_COLOURS.BG4 }}>
                        <tr>
                          {['Date', 'Items', 'Brands', 'Qty', 'Avg Sale', 'Total'].map(h => (
                            <th key={h} className="px-3 py-2 text-left text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSales.map((sale, i) => (
                          <tr
                            key={i}
                            style={{
                              backgroundColor: i % 2 === 0 ? 'transparent' : `${APP_COLOURS.BG4}40`,
                              borderBottom: `1px solid ${APP_COLOURS.BG4}`,
                            }}
                          >
                            <td className="px-3 py-2 text-xs whitespace-nowrap" style={{ color: APP_COLOURS.BLUE }}>
                              {moment(sale.dateCreated).format('DD MMM YY')}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex flex-col gap-0.5">
                                {(sale.items || []).map((item, j) => (
                                  <span key={j} className="text-xs" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                                    {item.name || 'Item'} ×{item.quantity || 1}
                                    {item.returned && (
                                      <span className="ml-1.5 text-xs px-1 rounded" style={{ backgroundColor: `${APP_COLOURS.ORANGE}20`, color: APP_COLOURS.ORANGE }}>
                                        Returned
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                              {(sale.brands || []).join(', ') || '--'}
                            </td>
                            <td className="px-3 py-2 text-xs text-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                              {sale.ipt || (sale.items && sale.items.length) || 0}
                            </td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                              {currencyFormat(sale.ars || 0)}
                            </td>
                            <td className="px-3 py-2 text-xs font-semibold whitespace-nowrap" style={{ color: APP_COLOURS.GREEN }}>
                              {currencyFormat(sale.trs || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-10 text-center">
                    <i className="fas fa-shopping-bag text-3xl mb-2" style={{ color: APP_COLOURS.BG5 }}></i>
                    <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>No sales for this period</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ---- FOOTER ACTIONS ---- */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0 gap-3"
          style={{ borderTop: `1px solid ${APP_COLOURS.BG4}`, backgroundColor: APP_COLOURS.BG3 }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded text-xs font-medium"
            style={{ backgroundColor: APP_COLOURS.BG4, color: APP_COLOURS.TEXTCOLOR }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = APP_COLOURS.BG5)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = APP_COLOURS.BG4)}
          >
            <i className="fas fa-times mr-1.5"></i>Close
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={handleExportExcel}
              disabled={!hasData}
              className="px-4 py-2 rounded text-xs font-semibold flex items-center gap-1.5"
              style={{
                backgroundColor: hasData ? '#1D6F42' : APP_COLOURS.BG5,
                color: '#fff',
                opacity: hasData ? 1 : 0.5,
                cursor: hasData ? 'pointer' : 'not-allowed',
              }}
              title={hasData ? 'Export to Excel (.xlsx)' : 'Generate report first'}
            >
              <i className="fas fa-file-excel"></i>
              Export Excel
            </button>

            <button
              onClick={handleExportPDF}
              disabled={!hasData}
              className="px-4 py-2 rounded text-xs font-semibold flex items-center gap-1.5"
              style={{
                backgroundColor: hasData ? APP_COLOURS.RED : APP_COLOURS.BG5,
                color: '#fff',
                opacity: hasData ? 1 : 0.5,
                cursor: hasData ? 'pointer' : 'not-allowed',
              }}
              title={hasData ? 'Export to PDF' : 'Generate report first'}
            >
              <i className="fas fa-file-pdf"></i>
              Export PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffSalesReport;
